import { useEffect } from 'react'

/*
 * Keyboard-safe inputs on touch devices. When the on-screen keyboard opens, `visualViewport` shrinks while the
 * layout viewport does not: we expose the difference as `--kb-inset` on <html> and `data-keyboard="open"`
 * (the bottom tab bar hides; fixed footers can lift), and scroll the focused field into the middle of what is
 * still visible.
 */
export const KEYBOARD_THRESHOLD_PX = 120

export function keyboardInset(layoutHeight: number, visualHeight: number, offsetTop = 0): number {
  return Math.max(0, Math.round(layoutHeight - visualHeight - offsetTop))
}

function isField(el: Element | null): el is HTMLElement {
  if (!el || !(el instanceof HTMLElement)) return false
  if (el.isContentEditable) return true
  if (el.tagName === 'TEXTAREA' || el.tagName === 'SELECT') return true
  if (el.tagName !== 'INPUT') return false
  return !/^(checkbox|radio|button|submit|reset|range|color|file)$/i.test((el as HTMLInputElement).type)
}

export function useKeyboardInset(): void {
  useEffect(() => {
    const vv = window.visualViewport
    const root = document.documentElement
    if (!vv) return
    const update = () => {
      const inset = keyboardInset(window.innerHeight, vv.height, vv.offsetTop)
      root.style.setProperty('--kb-inset', `${inset}px`)
      const open = inset > KEYBOARD_THRESHOLD_PX && isField(document.activeElement)
      if (open) root.dataset['keyboard'] = 'open'
      else delete root.dataset['keyboard']
    }
    let timer: ReturnType<typeof setTimeout> | undefined
    const onFocus = (e: FocusEvent) => {
      const t = e.target as Element | null
      if (!isField(t) || !window.matchMedia?.('(pointer: coarse)').matches) return
      clearTimeout(timer)
      // after the keyboard animation, keep the field clear of it
      timer = setTimeout(() => t.scrollIntoView({ block: 'center', behavior: 'smooth' }), 280)
    }
    vv.addEventListener('resize', update)
    vv.addEventListener('scroll', update)
    document.addEventListener('focusin', onFocus)
    document.addEventListener('focusout', update)
    update()
    return () => {
      clearTimeout(timer)
      vv.removeEventListener('resize', update)
      vv.removeEventListener('scroll', update)
      document.removeEventListener('focusin', onFocus)
      document.removeEventListener('focusout', update)
    }
  }, [])
}
